import React, { useRef, useState } from 'react';
import { useFileUpload } from '@/hooks/mutations/file-upload/useFileUpload';
import { getMediaUrl } from '@/utils/common';
import { UploadCloud, X, Loader2, Image as ImageIcon, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils/styleUtils';
import { useToast } from '@/components/ui/use-toast';

interface FileUploadInputProps {
  value?: string;
  onChange: (url: string) => void;
  label?: string;
  accept?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const FileUploadInput: React.FC<FileUploadInputProps> = ({
  value,
  onChange,
  label,
  accept = 'image/*',
  className,
  placeholder = 'Faylni tanlang yoki shu yerga tashlang',
  disabled = false,
}) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { triggerFileUpload, isPending } = useFileUpload('/file');

  const handleFile = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await triggerFileUpload(formData);
      
      // Handle various response shapes
      let uploadedUrl = '';
      if (typeof res === 'string') {
        uploadedUrl = res;
      } else if (res?.data?.data) {
        uploadedUrl = typeof res.data.data === 'string' ? res.data.data : res.data.data.url || res.data.data.path || '';
      } else if (res?.data?.url) {
        uploadedUrl = res.data.url;
      } else if (res?.data?.path) {
        uploadedUrl = res.data.path;
      } else if (typeof res?.data === 'string') {
        uploadedUrl = res.data;
      } else if (res?.url) {
        uploadedUrl = res.url;
      } else if (res?.path) {
        uploadedUrl = res.path;
      }

      if (uploadedUrl) {
        onChange(uploadedUrl);
        toast({ title: 'Muvaffaqiyatli', description: 'Fayl yuklandi' });
      } else {
        toast({ title: 'Xatolik', description: 'Fayl yuklashda javob olinmadi', variant: 'destructive' });
      }
    } catch (err: any) {
      toast({
        title: 'Yuklashda xatolik',
        description: err?.response?.data?.message || err?.message || 'Faylni yuklab bo\'lmadi',
        variant: 'destructive',
      });
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled || isPending) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
    // reset input value so re-selecting same file triggers change
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const isPdf = value?.toLowerCase().endsWith('.pdf') || value?.includes('.pdf');

  return (
    <div className={cn('space-y-1.5', className)}>
      {label && <label className="text-xs font-semibold text-slate-700 block">{label}</label>}

      {value ? (
        <div className="relative group border border-slate-200 rounded-xl p-2 bg-slate-50 flex items-center justify-between gap-3 overflow-hidden">
          <div className="flex items-center gap-3 min-w-0">
            {isPdf ? (
              <div className="size-12 rounded-lg bg-red-100 text-red-600 flex items-center justify-center shrink-0">
                <FileText className="size-6" />
              </div>
            ) : (
              <div className="size-12 rounded-lg bg-white border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                <img
                  src={getMediaUrl(value)}
                  alt="Yuklangan fayl"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLElement).style.display = 'none';
                  }}
                />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-800 truncate max-w-[200px] sm:max-w-[300px]">
                {value.split('/').pop() || value}
              </p>
              <a
                href={getMediaUrl(value)}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-primary hover:underline block truncate"
              >
                Ko'rish (ochish)
              </a>
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled || isPending}
              className="h-8 px-2.5 rounded-lg text-xs"
            >
              Almashtirish
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onChange('')}
              disabled={disabled || isPending}
              className="size-8 rounded-lg text-slate-500 hover:text-red-500 hover:bg-red-50"
            >
              <X className="size-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => !disabled && !isPending && fileInputRef.current?.click()}
          className={cn(
            'border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center text-center cursor-pointer transition-all',
            isDragging ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-primary/50 bg-slate-50/50 hover:bg-slate-50',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          {isPending ? (
            <div className="flex flex-col items-center gap-2 py-2">
              <Loader2 className="size-6 text-primary animate-spin" />
              <p className="text-xs font-medium text-slate-600">Fayl yuklanmoqda...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1.5 py-1">
              <div className="size-9 rounded-full bg-blue-50 text-primary flex items-center justify-center">
                <UploadCloud className="size-5" />
              </div>
              <p className="text-xs font-medium text-slate-700">{placeholder}</p>
              <p className="text-[11px] text-slate-400">Device (qurilma)dan tanlang yoki bu yerga sudrab tashlang</p>
            </div>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="hidden"
        disabled={disabled || isPending}
      />
    </div>
  );
};
